import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Button, LinearProgress, Alert, Stack,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, MenuItem, InputAdornment, Drawer, Badge,
  ToggleButton, ToggleButtonGroup, Radio, RadioGroup, FormControlLabel, FormControl, Checkbox,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Science as ScienceIcon,
  Delete as DeleteIcon,
  Place as PlaceIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  ArrowOutward as ArrowOutwardIcon,
  StickyNote2 as StickyNote2Icon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ─── Dispatch Calendar (Preview Build) ───────────────────────────────────────
// Mike Grady spec, Oct 2026. Isolated from live /dispatch.
// Visual pass Oct 21 2026: card-based modern look, status accents (not fills),
// soft shadows, system font stack, generous whitespace, hover transitions.

// ACS Brand design tokens
// Source: acs-brand skill (ACS Blue #1A5490, Dark Blue #0D2C54, Charcoal #2D2D2D,
// Heebo typeface with Montserrat fallback). Spacing follows 8pt scale.
const T = {
  fontStack: '"Heebo", "Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  text: '#2D2D2D',          // Charcoal (brand body text)
  textMuted: '#4A4A4A',     // Dark Gray
  textFaint: '#808080',     // Medium Gray
  bg: '#F2F2F2',            // Off White (brand neutral background)
  surface: '#FFFFFF',
  border: '#D9D9D9',        // Pale Gray
  borderSoft: '#EDEDED',
  accent: '#1A5490',        // ACS Blue (primary brand)
  accentDark: '#0D2C54',    // ACS Dark Blue (hover/pressed)
  accentBright: '#0066CC',  // Bright Blue
  cta: '#FF6B35',           // Brand Orange (sparingly, CTAs only)
  teal: '#00A8B5',          // Brand Teal (secondary accent)
  shadowSm: '0 1px 2px rgba(13,44,84,0.05)',
  shadowMd: '0 1px 3px rgba(13,44,84,0.07), 0 4px 12px rgba(13,44,84,0.05)',
  shadowLg: '0 4px 12px rgba(13,44,84,0.09), 0 12px 32px rgba(13,44,84,0.07)',
  radius: 4,                // Brand standard: 4px for buttons/cards
  radiusSm: 4,
  radiusLg: 8,
  transition: 'all 150ms ease',
};

// Status palette — subtle accents, NOT full-cell fills.
// Status greens/ambers/reds kept functional (operational semantics) but muted
// to coexist with ACS Blue brand palette.
const S = {
  confirmed: { dot: '#2E7D32', border: '#A5D6A7', tint: '#F1F8F2', text: '#1B5E20', label: 'Confirmed' },
  suggested: { dot: '#F57C00', border: '#FFCC80', tint: '#FFF8F0', text: '#E65100', label: 'Suggested' },
  flagged:   { dot: '#C62828', border: '#EF9A9A', tint: '#FDF2F2', text: '#B71C1C', label: 'Flagged'   },
  needs_pm:  { dot: '#CA8A04', border: '#FDE68A', tint: '#FEFCE8', text: '#854D0E', label: 'PM: finish this' },
};
const statusStyle = (status) => S[status] || S.suggested;

// Avatar palette — ACS brand family (blues + supporting neutrals/accents).
// Deterministic per name but all tones live within the brand system.
const AVATAR_PALETTE = [
  { bg: '#E8EEF6', fg: '#1A5490' },  // ACS Blue tint
  { bg: '#DCE6F2', fg: '#0D2C54' },  // Dark Blue tint
  { bg: '#D6E4F5', fg: '#0066CC' },  // Bright Blue tint
  { bg: '#D9F0F2', fg: '#00828E' },  // Teal tint
  { bg: '#F2F2F2', fg: '#2D2D2D' },  // Charcoal on Off White
  { bg: '#E6ECF2', fg: '#001F3F' },  // Navy tint
  { bg: '#FFE8DC', fg: '#C84A1C' },  // Orange tint (sparingly)
  { bg: '#E0E0E0', fg: '#4A4A4A' },  // Dark Gray on Light Gray
];
const avatarFor = (name) => {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};
const initials = (name) => String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();

function startOfWeek(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  dt.setHours(0, 0, 0, 0);
  return dt;
}
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; };
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtDay = (d) => d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// ─── Status dot (shared) ──────────────────────────────────────────────────────
const StatusDot = ({ status, size = 8 }) => {
  const s = statusStyle(status);
  return (
    <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: s.dot, flexShrink: 0, boxShadow: `0 0 0 2px ${s.tint}` }} />
  );
};

// ─── Avatar chip ──────────────────────────────────────────────────────────────
const AvatarChip = ({ name, size = 32 }) => {
  const c = avatarFor(name);
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%',
      bgcolor: c.bg, color: c.fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, fontSize: size * 0.4, fontFamily: T.fontStack, flexShrink: 0,
    }}>
      {initials(name) || '?'}
    </Box>
  );
};

// ─── Address autocomplete ─────────────────────────────────────────────────────
const AddressPicker = ({ value, onChange }) => {
  const [input, setInput]     = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!input || input.length < 2) { setOptions([]); return; }
    const token = localStorage.getItem('token');
    const h = setTimeout(() => {
      setLoading(true);
      axios.get('/api/dispatch-preview/address-search', {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: input },
      })
        .then((r) => setOptions(r.data?.results || []))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(h);
  }, [input]);

  const sourceChip = (src) => {
    const map = {
      sage:            { label: 'Sage',      bg: '#DBEAFE', fg: '#1D4ED8' },
      dtools:          { label: 'D-Tools',   bg: '#F3E8FF', fg: '#7E22CE' },
      cc:              { label: 'CC',        bg: '#E2E8F0', fg: '#334155' },
      'sage-customer': { label: 'AfterCare', bg: '#FEF3C7', fg: '#B45309' },
      manual:          { label: 'Manual',    bg: '#DCFCE7', fg: '#15803D' },
    };
    const s = map[src] || { label: src, bg: '#F1F5F9', fg: '#475569' };
    return <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.fg, fontSize: 10, height: 18, ml: 1, borderRadius: 1, fontWeight: 600 }} />;
  };

  // Build options with a synthetic "+ Use typed address as manual" row at the
  // top whenever the user has typed something that doesn't exactly match an
  // existing result. Lets PMs enter addresses for jobs not yet in Sage /
  // D-Tools / Command Center. Tom Clancy 2026-11-02.
  const trimmed = (input || '').trim();
  const hasExactMatch = options.some(
    (o) => (o.site_address || '').toLowerCase() === trimmed.toLowerCase()
            || (o.job_label || '').toLowerCase() === trimmed.toLowerCase()
  );
  const augmentedOptions = (trimmed.length >= 2 && !hasExactMatch)
    ? [{ __manual__: true, site_address: trimmed, source: 'manual' }, ...options]
    : options;

  return (
    <Autocomplete
      options={augmentedOptions}
      value={value}
      freeSolo
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      autoSelect
      onChange={(_, v) => {
        if (!v) return onChange(null);
        // Free-typed string (freeSolo) — user typed and tabbed/blurred away
        // without picking a dropdown option. Treat it as a manual address.
        // Fix 2026-11-03: previously users had to click the "+ Use ... as a
        // manual address" option in the dropdown; many assumed typing + Save
        // would work. Now it does.
        if (typeof v === 'string') {
          const trimmedV = v.trim();
          if (!trimmedV) return onChange(null);
          return onChange({
            job_number:   null,
            job_label:    null,
            site_address: trimmedV,
            site_city:    null,
            site_state:   null,
            source:       'manual',
          });
        }
        if (v.__manual__) {
          onChange({
            job_number:   null,
            job_label:    null,
            site_address: v.site_address,
            site_city:    null,
            site_state:   null,
            source:       'manual',
          });
        } else {
          onChange(v);
        }
      }}
      onInputChange={(_, v) => setInput(v)}
      loading={loading}
      getOptionLabel={(o) => {
        if (typeof o === 'string') return o;
        return o?.job_label || o?.site_address || '';
      }}
      isOptionEqualToValue={(a, b) => (a?.job_number || a?.site_address) === (b?.job_number || b?.site_address)}
      filterOptions={(x) => x}
      noOptionsText={input.length < 2 ? 'Type to search…' : `Press Enter to use “${input}” as a manual address`}
      renderInput={(params) => {
        const existingStart = params.InputProps?.startAdornment;
        return (
          <TextField {...params} label="Job / Address *" placeholder="Client name, project, address, or job #"
            InputProps={{
              ...(params.InputProps || {}),
              startAdornment: (
                <>
                  <InputAdornment position="start"><PlaceIcon fontSize="small" sx={{ color: T.textFaint }} /></InputAdornment>
                  {existingStart}
                </>
              ),
            }}
          />
        );
      }}
      renderOption={(props, o) => {
        if (o.__manual__) {
          return (
            <li {...props} key="__manual__" style={{ borderBottom: `1px dashed ${T.borderSoft}` }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#15803D', fontFamily: T.fontStack }}>
                  + Use “{o.site_address}” as a manual address
                  <Chip size="small" label="Manual" sx={{ bgcolor: '#DCFCE7', color: '#15803D', fontSize: 10, height: 18, ml: 1, borderRadius: 1, fontWeight: 600 }} />
                </Typography>
                <Typography variant="caption" sx={{ color: T.textMuted }}>
                  Pick this if the job isn’t in Sage / D-Tools / Command Center yet.
                </Typography>
              </Box>
            </li>
          );
        }
        return (
          <li {...props} key={`${o.job_number || o.job_label}-${o.site_address}`}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: T.fontStack }}>
                {o.job_label || o.site_address}
                {sourceChip(o.source)}
                {o.job_number && <Typography component="span" variant="caption" sx={{ ml: 1, color: T.textMuted, fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>{o.job_number}</Typography>}
              </Typography>
              <Typography variant="caption" sx={{ color: T.textMuted }}>
                {o.site_address}{o.site_city ? `, ${o.site_city}` : ''}{o.site_state ? ` ${o.site_state}` : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
    />
  );
};

// ─── Create / Edit modal ─────────────────────────────────────────────────────
const BlockModal = ({ open, onClose, techs, pms, library, authority, me, existing, defaults, focusDate, onSaved }) => {
  const isEdit = !!existing;
  const [tech, setTech]           = useState('');
  // Additional technicians (create-only). Each gets its own copy of the
  // block with identical payload — fan-out happens client-side.
  const [extraTechs, setExtraTechs] = useState([]); // array of tech.id
  const [addr, setAddr]           = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [startHour, setStartHour] = useState('');
  const [endHour, setEndHour]     = useState('');
  const [taskType, setTaskType]   = useState('');
  const [customTask, setCustom]   = useState('');
  const [notes, setNotes]         = useState('');
  const [pmId, setPmId]           = useState('');
  const [reportTo, setReportTo]   = useState('job_site');
  const [reportToNote, setReportToNote] = useState('');
  const [repeatMode, setRepeatMode] = useState('none'); // 'none' | 'daily' | 'weekly'
  const [repeatDays, setRepeatDays] = useState([]);     // [0..6] when weekly
  const [repeatCount, setRepeatCount] = useState(4);    // number of occurrences
  const [blockKind, setBlockKind] = useState('assignment');
  const [offPortion, setOffPortion] = useState('full');
  const [siteContactName,  setSiteContactName]  = useState('');
  const [siteContactPhone, setSiteContactPhone] = useState('');
  const [siteContactEmail, setSiteContactEmail] = useState('');
  const [supportDepts, setSupportDepts] = useState([]); // ['Programming','Lighting','Shading','IT']
  const [supportNotes, setSupportNotes] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [needsPm, setNeedsPm] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);

  // Self-assignment = the authenticated user is the technician for this block.
  // When true, suppress authority-change warning (per Tom 10/21): PMs scheduling
  // their own time are reporting what they're doing, not editing someone else.
  const myId = me?.user?.id;
  const isSelf = myId != null && Number(tech) === Number(myId);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setTech(existing.tech_id || '');
      setAddr({
        job_number: existing.job_number,
        job_label: existing.job_label,
        site_address: existing.site_address,
        site_city: existing.site_city,
        site_state: existing.site_state,
        source: existing.address_source,
      });
      setStartDate(existing.start_date?.slice(0, 10) || '');
      setEndDate(existing.end_date?.slice(0, 10) || '');
      setStartHour(existing.start_hour != null ? String(existing.start_hour).padStart(2, '0') + ':00' : '');
      setEndHour(existing.end_hour     != null ? String(existing.end_hour).padStart(2, '0')   + ':00' : '');
      setTaskType(existing.task_type || '');
      setNotes(existing.task_notes || '');
      setPmId(existing.pm_id || '');
      setReportTo(existing.report_to_location || 'job_site');
      setReportToNote(existing.report_to_note || '');
      setBlockKind(existing.block_kind || 'assignment');
      setOffPortion(existing.off_portion || 'full');
      setSiteContactName(existing.site_contact_name || '');
      setSiteContactPhone(existing.site_contact_phone || '');
      setSiteContactEmail(existing.site_contact_email || '');
      setSupportDepts(Array.isArray(existing.support_departments) ? existing.support_departments : []);
      setSupportNotes(existing.support_notes || '');
      setIncludeWeekends(false);
      setNeedsPm(existing.status === 'needs_pm');
      setRepeatMode('none');  // never on edit
      setRepeatDays([]);
      setRepeatCount(4);
    } else {
      setTech(defaults?.techId || '');
      setExtraTechs([]);
      setAddr(null);
      setStartDate(defaults?.date || ymd(new Date()));
      setEndDate(defaults?.date || ymd(new Date()));
      setStartHour('08:00');
      setEndHour('17:00');
      setTaskType('');
      setCustom('');
      setNotes('');
      setPmId('');
      setReportTo('job_site');
      setReportToNote('');
      setBlockKind('assignment');
      setOffPortion('full');
      setSiteContactName(defaults?.site_contact_name || '');
      setSiteContactPhone(defaults?.site_contact_phone || '');
      setSiteContactEmail(defaults?.site_contact_email || '');
      setSupportDepts([]);
      setSupportNotes('');
      setIncludeWeekends(false);
      setNeedsPm(false);
      setRepeatMode('none');
      setRepeatDays([]);
      setRepeatCount(4);
    }
    setExtraTechs([]); // extras are additive on both create and edit
    setError(null);
  }, [open, isEdit, existing, defaults]);

  const flatTasks = useMemo(() => {
    const items = [];
    (library || []).forEach((g) => g.tasks.forEach((t) => items.push({ group: g.group, label: t })));
    items.push({ group: 'Other', label: '— Custom task (type below) —', custom: true });
    return items;
  }, [library]);

  const handleSave = async () => {
    setError(null);
    // For holiday, no tech required (server fans out). For others, tech is required.
    if (blockKind !== 'holiday' && !tech) return setError('Pick a technician');
    // De-dupe + remove the primary from extras in case it slipped in
    const extras = Array.from(new Set(extraTechs.filter((id) => id && Number(id) !== Number(tech))));
    if (!startDate || !endDate) return setError('Pick start/end dates');
    if (new Date(endDate) < new Date(startDate)) return setError('End date must be on or after start');
    if (blockKind === 'assignment' && reportTo === 'other' && !reportToNote.trim()) return setError('Specify the "Other" report-to location');
    if (!isEdit && repeatMode === 'weekly' && repeatDays.length === 0) return setError('Pick at least one day of the week to repeat on');
    if (blockKind === 'note' && !notes.trim()) return setError('Note text is required');
    if (blockKind === 'assignment' && !notes.trim()) return setError('Notes are required — add context for the tech');
    if (blockKind === 'time_off' && !notes.trim()) return setError('Notes are required — add a reason for the time off');
    if (blockKind === 'assignment' && !siteContactName.trim())  return setError('Primary site contact name is required');
    if (blockKind === 'assignment' && !siteContactPhone.trim()) return setError('Primary site contact phone is required');
    const effectiveTask = taskType && taskType !== '— Custom task (type below) —' ? taskType : (customTask || null);

    const payload = {
      block_kind: blockKind,
      start_date: startDate,
      end_date: endDate,
      task_notes: notes || null,
    };
    if (blockKind !== 'holiday') {
      payload.tech_id = tech;
    }
    if (blockKind === 'time_off') {
      payload.off_portion = offPortion;
    }
    if (blockKind === 'assignment') {
      payload.job_number = addr?.job_number || null;
      payload.job_label = addr?.job_label || null;
      payload.site_address = addr?.site_address || null;
      payload.site_city = addr?.site_city || null;
      payload.site_state = addr?.site_state || null;
      payload.address_source = addr?.source || null;
      payload.task_type = effectiveTask;
      payload.pm_id = pmId || null;
      payload.report_to_location = reportTo || null;
      payload.report_to_note = reportTo === 'other' ? (reportToNote || null) : null;
      payload.start_hour = startHour ? parseInt(startHour.split(':')[0], 10) : null;
      payload.end_hour   = endHour   ? parseInt(endHour.split(':')[0],   10) : null;
      payload.site_contact_name  = siteContactName.trim();
      payload.site_contact_phone = siteContactPhone.trim();
      payload.site_contact_email = siteContactEmail.trim() || null;
      payload.support_departments = supportDepts.length ? supportDepts : null;
      payload.support_notes = supportDepts.length && supportNotes.trim() ? supportNotes.trim() : null;
    }
    if (blockKind === 'holiday') {
      payload.job_label = notes.trim() || null; // use notes field as holiday label
      payload.task_notes = null;
    }
    if (!isEdit && repeatMode !== 'none') {
      payload.recurrence = {
        frequency: repeatMode,
        count: Math.max(1, Math.min(52, parseInt(repeatCount, 10) || 1)),
        days: repeatMode === 'weekly' ? repeatDays : undefined,
      };
    }
    // Weekend guard (Tom 10/30): if range crosses Sat/Sun and user didn't
    // check "Include weekend days", confirm or auto-split.
    if (!includeWeekends && (blockKind === 'assignment' || blockKind === 'time_off')) {
      const s = new Date(`${startDate}T12:00:00`);
      const e = new Date(`${endDate}T12:00:00`);
      let crossesWeekend = false;
      const cur = new Date(s);
      while (cur <= e) {
        if (cur.getDay() === 0 || cur.getDay() === 6) { crossesWeekend = true; break; }
        cur.setDate(cur.getDate() + 1);
      }
      if (crossesWeekend) {
        const ok = window.confirm(
          'Heads up: this block spans a weekend.\n\n' +
          'Saturday and Sunday will be SKIPPED automatically (ACS does not dispatch on weekends).\n\n' +
          'If you actually want to assign weekend work, cancel and check "Include weekend days" first.'
        );
        if (!ok) { setSaving(false); return; }
      }
    }
    payload.include_weekends = !!includeWeekends;
    if (!isEdit && authority === 'confirmed' && blockKind === 'assignment') {
      payload.needs_pm = !!needsPm;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (isEdit) {
        // Update the existing block's primary tech + fields
        await axios.put(`/api/dispatch-preview/blocks/${existing.id}`, payload, authHeaders);
      } else {
        // Create primary block first
        await axios.post('/api/dispatch-preview/blocks', payload, authHeaders);
      }
      // Fan out to additional techs on both create AND edit. Each extra tech
      // gets its own new block that mirrors the current form state. On edit,
      // this is how a user adds additional techs to an existing assignment
      // after the fact (per Tom Clancy 2026-10-29).
      if (extras.length > 0) {
        // Strip recurrence on edit fan-out — recurrence is a create-only concept
        // and the field is already disabled in the UI on edit.
        const extraPayload = { ...payload };
        if (isEdit) delete extraPayload.recurrence;
        const results = await Promise.allSettled(
          extras.map((extraTechId) =>
            axios.post('/api/dispatch-preview/blocks', { ...extraPayload, tech_id: extraTechId }, authHeaders)
          )
        );
        const failed = results
          .map((r, i) => ({ r, techId: extras[i] }))
          .filter(({ r }) => r.status === 'rejected');
        if (failed.length > 0) {
          const names = failed
            .map(({ techId }) => (techs.find((t) => Number(t.id) === Number(techId))?.name || `tech ${techId}`))
            .join(', ');
          // Primary saved; some extras failed. Surface it but keep the
          // modal open so the user can see what happened.
          setError(`${isEdit ? 'Changes saved' : 'Primary assignment saved'}. Could not assign: ${names}. ${failed[0].r.reason?.response?.data?.error || failed[0].r.reason?.message || ''}`);
          onSaved();
          setSaving(false);
          return;
        }
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this block? (Removes ALL days it spans.)')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/dispatch-preview/blocks/${existing.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Multi-day blocks: let the user remove just the day they opened from the
  // grid. Shrinks or splits the block server-side (Andrew W. 2026-11-02).
  const spansMultipleDays = isEdit
    && existing?.start_date && existing?.end_date
    && String(existing.start_date).slice(0, 10) !== String(existing.end_date).slice(0, 10);
  const focusDateInRange = focusDate
    && spansMultipleDays
    && focusDate >= String(existing.start_date).slice(0, 10)
    && focusDate <= String(existing.end_date).slice(0, 10);
  const handleDeleteDay = async () => {
    if (!isEdit || !focusDateInRange) return;
    const pretty = (() => {
      try { return new Date(focusDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
      catch { return focusDate; }
    })();
    if (!window.confirm(`Remove just ${pretty} from this block? Other days in the span stay.`)) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/dispatch-preview/blocks/${existing.id}/day/${focusDate}`, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || e.message);
    }
  };

  const handleConfirm = async () => {
    if (!isEdit || !existing?.id) return;
    const token = localStorage.getItem('token');
    try {
      await axios.post(`/api/dispatch-preview/blocks/${existing.id}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } });
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const authorityPill = (txt, kind) => {
    const s = statusStyle(kind);
    return (
      <Chip size="small" label={txt}
        sx={{ ml: 1.5, bgcolor: s.tint, color: s.text, fontWeight: 600, border: `1px solid ${s.border}`, borderRadius: 1.5 }} />
    );
  };

  const SectionLabel = ({ children }) => (
    <Typography sx={{
      fontSize: 11, fontWeight: 700, color: T.textMuted,
      textTransform: 'uppercase', letterSpacing: 0.9,
      mb: 1.25, mt: 0.5,
    }}>
      {children}
    </Typography>
  );

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: `${T.radiusSm}px`, bgcolor: T.surface, fontFamily: T.fontStack },
    '& .MuiInputLabel-root': { fontFamily: T.fontStack, fontSize: 14 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.textMuted },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: `${T.radius}px`, boxShadow: T.shadowLg, fontFamily: T.fontStack } }}>
      <DialogTitle sx={{
        fontFamily: T.fontStack, fontWeight: 600, color: T.text,
        fontSize: 20, px: 3, pt: 3, pb: 1.5,
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
      }}>
        {isEdit ? 'Edit Dispatch Block' : 'Create Dispatch Block'}
        {/* Self-assignment: no authority warning — PMs reporting their own time */}
        {!isSelf && authority === 'confirmed' && authorityPill('Will be CONFIRMED', 'confirmed')}
        {!isSelf && authority === 'suggest'   && authorityPill('Will be SUGGESTED', 'suggested')}
        {!isSelf && isEdit && existing?.status === 'confirmed' && authority !== 'confirmed' && (
          authorityPill('Editing confirmed → will FLAG', 'flagged')
        )}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 1, pb: 2.5 }}>
        {/* Block type selector */}
        <SectionLabel>Type</SectionLabel>
        <ToggleButtonGroup
          exclusive value={blockKind}
          onChange={(_, v) => { if (v) setBlockKind(v); }}
          size="small"
          sx={{ mb: 2.5, flexWrap: 'wrap', gap: 0.5,
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px !important`, px: 2, fontSize: 13, fontFamily: T.fontStack, border: `1px solid ${T.border} !important`, color: T.textMuted },
            '& .MuiToggleButton-root.Mui-selected': { bgcolor: T.accent, color: '#fff', borderColor: `${T.accent} !important` },
          }}
        >
          <ToggleButton value="assignment">Assignment</ToggleButton>
          <ToggleButton value="time_off">Time Off</ToggleButton>
          <ToggleButton value="holiday">Holiday</ToggleButton>
          <ToggleButton value="note">Note</ToggleButton>
        </ToggleButtonGroup>

        {/* Section 1: Who */}
        {blockKind !== 'holiday' && <SectionLabel>Who</SectionLabel>}
        {blockKind === 'holiday' && (
          <Box sx={{ mb: 2, px: 1.5, py: 1, borderRadius: `${T.radiusSm}px`, bgcolor: '#FFF4E5', border: '1px solid #FFB300' }}>
            <Typography sx={{ fontSize: 13, color: '#7A4A00', fontWeight: 500 }}>
              Holiday blocks are created for <strong>all active technicians</strong> automatically.
            </Typography>
          </Box>
        )}
        {blockKind !== 'holiday' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField select fullWidth label="Technician *" value={tech}
            onChange={(e) => setTech(e.target.value)} sx={inputSx}>
            {techs.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                <Typography component="span" variant="caption" sx={{ ml: 1, color: T.textMuted }}>
                  {t.role}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          {blockKind === 'assignment' && (
          <TextField select fullWidth label="Assigned by PM (optional)" value={pmId}
            onChange={(e) => setPmId(e.target.value)} sx={inputSx}>
            <MenuItem value=""><em>— none —</em></MenuItem>
            {pms.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          )}
        </Box>
        )}

        {/* Time Off: off_portion radio */}
        {blockKind === 'time_off' && (
          <FormControl sx={{ mt: 1.5 }}>
            <RadioGroup row value={offPortion} onChange={(e) => setOffPortion(e.target.value)}>
              <FormControlLabel value="full" control={<Radio size="small" />} label="Full day" sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, fontFamily: T.fontStack } }} />
              <FormControlLabel value="am"   control={<Radio size="small" />} label="AM only" sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, fontFamily: T.fontStack } }} />
              <FormControlLabel value="pm"   control={<Radio size="small" />} label="PM only" sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, fontFamily: T.fontStack } }} />
            </RadioGroup>
          </FormControl>
        )}

        {/* Additional technicians (create-only). Everything else on the block
            — job, address, times, task, PM, report-to, repeat — is copied as-is
            to each additional tech. Fan-out happens client-side via one POST
            per tech. */}
        {blockKind === 'assignment' && (
          <Box sx={{ mt: 1.5 }}>
            {extraTechs.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {extraTechs.map((id, idx) => {
                  const t = techs.find((x) => Number(x.id) === Number(id));
                  if (!t) return null;
                  return (
                    <Chip
                      key={`${id}-${idx}`}
                      label={<span><strong>{t.name}</strong> <span style={{ color: T.textMuted, fontWeight: 400 }}>· {t.role}</span></span>}
                      onDelete={() => setExtraTechs(extraTechs.filter((_, i) => i !== idx))}
                      sx={{ bgcolor: '#E8EEF6', color: T.accent, border: `1px solid ${T.border}`, borderRadius: 1, '& .MuiChip-deleteIcon': { color: T.accent } }}
                    />
                  );
                })}
              </Box>
            )}
            <Autocomplete
              options={techs.filter((t) =>
                Number(t.id) !== Number(tech) && !extraTechs.some((e) => Number(e) === Number(t.id))
              )}
              getOptionLabel={(o) => `${o.name} · ${o.role}`}
              value={null}
              onChange={(_e, picked) => { if (picked) setExtraTechs([...extraTechs, picked.id]); }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={isEdit ? '+ Add another technician to this assignment' : '+ Assign to additional technician (optional)'}
                  sx={{
                    ...inputSx,
                    '& .MuiOutlinedInput-root': {
                      ...inputSx['& .MuiOutlinedInput-root'],
                      bgcolor: T.bg,
                    },
                  }}
                />
              )}
              sx={{ maxWidth: 420 }}
              disabled={!tech}
            />
            {!tech && (
              <Typography sx={{ fontSize: 11, color: T.textFaint, mt: 0.5, ml: 0.5 }}>
                Pick a primary technician first.
              </Typography>
            )}
            {tech && extraTechs.length > 0 && (
              <Typography sx={{ fontSize: 11, color: T.textMuted, mt: 0.5, ml: 0.5 }}>
                {isEdit
                  ? `On save, a new matching block will be created for ${extraTechs.length === 1 ? 'this additional technician' : `these ${extraTechs.length} additional technicians`} (current fields copied).`
                  : `All fields below will be copied to ${extraTechs.length === 1 ? 'this additional technician' : `these ${extraTechs.length} additional technicians`}.`}
              </Typography>
            )}
          </Box>
        )}

        {/* Section 2: Where */}
        <SectionLabel>Where — Job Site</SectionLabel>
        <Box sx={{ '& .MuiAutocomplete-root': inputSx }}>
          <AddressPicker value={addr} onChange={setAddr} />
        </Box>
        {addr && (addr.site_address || addr.job_label) && (
          <Box sx={{
            mt: 1, px: 1.75, py: 1.25, borderRadius: `${T.radiusSm}px`,
            bgcolor: '#F0F9FF', border: '1px solid #BAE6FD',
            display: 'flex', alignItems: 'flex-start', gap: 1,
          }}>
            <PlaceIcon fontSize="small" sx={{ color: '#0369A1', mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E' }}>
                {addr.job_label || addr.site_address}
              </Typography>
              {addr.site_address && (
                <Typography sx={{ fontSize: 12, color: '#075985' }}>
                  {addr.site_address}{addr.site_city ? `, ${addr.site_city}` : ''}{addr.site_state ? ` ${addr.site_state}` : ''}
                </Typography>
              )}
              {addr.job_number && (
                <Typography sx={{ fontSize: 11, color: '#0369A1', fontFamily: 'ui-monospace, SFMono-Regular, monospace', mt: 0.25 }}>
                  Job #{addr.job_number}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Section: Report-To Location */}
        <SectionLabel>Report To</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField select fullWidth label="Report-to location *" value={reportTo}
            onChange={(e) => setReportTo(e.target.value)} sx={inputSx}>
            <MenuItem value="job_site">Job Site</MenuItem>
            <MenuItem value="acs_westbury">ACS Westbury</MenuItem>
            <MenuItem value="acs_nyc">ACS NYC</MenuItem>
            <MenuItem value="other">Other — specify</MenuItem>
          </TextField>
          {reportTo === 'other' && (
            <TextField fullWidth label="Specify other location *"
              value={reportToNote} onChange={(e) => setReportToNote(e.target.value)}
              sx={inputSx} placeholder="Warehouse pickup, vendor site, etc." />
          )}
        </Box>

        {/* Section 3: When */}
        <SectionLabel>When</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField fullWidth type="date" label="Start Date *" InputLabelProps={{ shrink: true }}
            value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={inputSx} />
          <TextField fullWidth type="date" label="End Date *" InputLabelProps={{ shrink: true }}
            value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={inputSx} />
          <TextField fullWidth type="time" label="Start Time" InputLabelProps={{ shrink: true }}
            value={startHour} onChange={(e) => setStartHour(e.target.value)} sx={inputSx}
            inputProps={{ step: 1800 }} />
          <TextField fullWidth type="time" label="End Time" InputLabelProps={{ shrink: true }}
            value={endHour} onChange={(e) => setEndHour(e.target.value)} sx={inputSx}
            inputProps={{ step: 1800 }} />
        </Box>
        {startHour && endHour && (() => {
          const [sh, sm] = startHour.split(':').map(Number);
          const [eh, em] = endHour.split(':').map(Number);
          const mins = (eh * 60 + em) - (sh * 60 + sm);
          if (mins <= 0) return null;
          const h = Math.floor(mins / 60); const m = mins % 60;
          return (
            <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 1, pl: 0.25 }}>
              Duration: <strong style={{ color: T.text }}>{h}h{m ? ` ${m}m` : ''}</strong>
            </Typography>
          );
        })()}

        {/* Section: Repeat (create-only, hidden on edit) */}
        {!isEdit && (
          <>
            <SectionLabel>Repeat</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField select fullWidth label="Repeats" value={repeatMode}
                onChange={(e) => setRepeatMode(e.target.value)} sx={inputSx}>
                <MenuItem value="none">Does not repeat</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly on selected day(s)</MenuItem>
              </TextField>
              {repeatMode !== 'none' && (
                <TextField fullWidth type="number" label="Occurrences" value={repeatCount}
                  onChange={(e) => setRepeatCount(e.target.value)}
                  inputProps={{ min: 1, max: 52 }} sx={inputSx}
                  helperText="Max 60 total blocks" />
              )}
            </Box>
            {repeatMode === 'weekly' && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {[
                  { d: 1, label: 'Mon' },
                  { d: 2, label: 'Tue' },
                  { d: 3, label: 'Wed' },
                  { d: 4, label: 'Thu' },
                  { d: 5, label: 'Fri' },
                  { d: 6, label: 'Sat' },
                  { d: 0, label: 'Sun' },
                ].map(({ d, label }) => {
                  const on = repeatDays.includes(d);
                  return (
                    <Box key={d}
                      onClick={() => setRepeatDays(on ? repeatDays.filter((x) => x !== d) : [...repeatDays, d])}
                      sx={{
                        px: 1.5, py: 0.75, borderRadius: `${T.radiusSm}px`,
                        border: `1px solid ${on ? T.accent : T.border}`,
                        bgcolor: on ? T.accent : T.surface,
                        color: on ? '#fff' : T.text,
                        fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        transition: T.transition,
                        userSelect: 'none',
                        '&:hover': { borderColor: T.textMuted },
                      }}>
                      {label}
                    </Box>
                  );
                })}
              </Box>
            )}
            {repeatMode !== 'none' && (
              <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 1, pl: 0.25 }}>
                Will create <strong style={{ color: T.text }}>{
                  repeatMode === 'weekly' && repeatDays.length === 0 ? 0 :
                  Math.max(1, Math.min(52, parseInt(repeatCount, 10) || 1))
                }</strong> blocks starting {startDate}.
              </Typography>
            )}
          </>
        )}

        {/* Section 4: What */}
        <SectionLabel>What</SectionLabel>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Autocomplete
            freeSolo
            options={flatTasks}
            groupBy={(o) => o.group}
            getOptionLabel={(o) => typeof o === 'string' ? o : o.label}
            value={taskType}
            onChange={(_, v) => {
              if (typeof v === 'string') setTaskType(v);
              else if (v?.custom) { setTaskType(''); }
              else setTaskType(v?.label || '');
            }}
            onInputChange={(_, v) => { if (!flatTasks.find((t) => t.label === v)) setTaskType(v); }}
            renderInput={(params) => (
              <TextField {...params} label="Task from library" placeholder="Rough-in, Service Call, …" sx={inputSx} />
            )}
          />
          <TextField fullWidth label="Or type a custom task" value={customTask}
            onChange={(e) => setCustom(e.target.value)} sx={inputSx}
            placeholder="If not in the task library" />
        </Box>

        {/* Primary Site Contact (assignment blocks only) */}
        {blockKind === 'assignment' && (
          <>
            <SectionLabel>Primary Site Contact</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField fullWidth required label="Contact name *"
                value={siteContactName} onChange={(e) => setSiteContactName(e.target.value)}
                error={!siteContactName.trim()}
                helperText={!siteContactName.trim() ? 'Required' : ' '}
                sx={inputSx} />
              <TextField fullWidth required label="Phone *"
                value={siteContactPhone} onChange={(e) => setSiteContactPhone(e.target.value)}
                error={!siteContactPhone.trim()}
                helperText={!siteContactPhone.trim() ? 'Required' : ' '}
                placeholder="(555) 123-4567" sx={inputSx} />
              <TextField fullWidth label="Email (optional)"
                value={siteContactEmail} onChange={(e) => setSiteContactEmail(e.target.value)}
                sx={inputSx} />
            </Box>
          </>
        )}

        {/* "Needs PM" toggle — confirmed-authority users only, on create, assignments only. */}
        {!isEdit && authority === 'confirmed' && blockKind === 'assignment' && (
          <Box sx={{ mb: 2, mt: 0.5, p: 1.5, borderRadius: `${T.radiusSm}px`,
            bgcolor: needsPm ? '#FEFCE8' : '#F8FAFC',
            border: `1px solid ${needsPm ? '#CA8A04' : T.border}` }}>
            <FormControlLabel
              control={<Checkbox checked={needsPm} onChange={(e) => setNeedsPm(e.target.checked)} size="small" sx={{ color: needsPm ? '#854D0E' : T.textMuted, '&.Mui-checked': { color: '#854D0E' } }} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: needsPm ? '#713F12' : T.text }}>
                    Needs PM to complete (yellow)
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: needsPm ? '#854D0E' : T.textMuted, mt: 0.25 }}>
                    {needsPm
                      ? 'Block will land as YELLOW — PM must add task, notes, site contact, and themselves as PM. Will auto-flip to GREEN once complete.'
                      : 'Check this if the assigned PM still owes task/notes/site contact details.'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Weekend inclusion toggle (assignment / time-off only). */}
        {(blockKind === 'assignment' || blockKind === 'time_off') && (
          <Box sx={{ mb: 2, mt: 0.5, p: 1.5, borderRadius: `${T.radiusSm}px`,
            bgcolor: includeWeekends ? '#FEF3C7' : '#F8FAFC',
            border: `1px solid ${includeWeekends ? '#F59E0B' : T.border}` }}>
            <FormControlLabel
              control={<Checkbox checked={includeWeekends} onChange={(e) => setIncludeWeekends(e.target.checked)} size="small" sx={{ color: includeWeekends ? '#B45309' : T.textMuted, '&.Mui-checked': { color: '#B45309' } }} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: includeWeekends ? '#78350F' : T.text }}>
                    Include weekend days (Sat / Sun)
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: includeWeekends ? '#92400E' : T.textMuted, mt: 0.25 }}>
                    {includeWeekends
                      ? 'ACS normally skips weekends — this block will be dispatched on Sat/Sun.'
                      : 'Sat/Sun will be skipped automatically if this block spans a weekend.'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Section 5: Notes */}
        <SectionLabel>Notes</SectionLabel>
        <TextField fullWidth multiline minRows={3}
          label={blockKind === 'holiday' ? 'Internal notes' : 'Internal notes *'}
          required={blockKind !== 'holiday'}
          error={blockKind !== 'holiday' && !notes.trim()}
          helperText={blockKind !== 'holiday' && !notes.trim() ? 'Notes are required' : ' '}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={inputSx}
          placeholder="Special instructions, parking, access codes, onsite contact…" />

        {/* Section 6: Department support (assignment only) */}
        {blockKind === 'assignment' && (
          <>
            <SectionLabel>Department support needed</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: supportDepts.length ? 1.5 : 2 }}>
              {['Programming', 'Lighting', 'Shading', 'IT'].map((d) => {
                const checked = supportDepts.includes(d);
                return (
                  <Chip
                    key={d}
                    label={d}
                    clickable
                    onClick={() => {
                      const next = checked ? supportDepts.filter((x) => x !== d) : [...supportDepts, d];
                      setSupportDepts(next);
                      // Seed support notes from task notes the first time any dept is checked.
                      if (!checked && !supportNotes && notes) setSupportNotes(notes);
                    }}
                    sx={{
                      borderRadius: 1,
                      fontWeight: 600,
                      bgcolor: checked ? T.accent : T.surface,
                      color: checked ? '#fff' : T.text,
                      border: `1px solid ${checked ? T.accent : T.border}`,
                      '&:hover': { bgcolor: checked ? T.accent : '#F0F4FA' },
                    }}
                  />
                );
              })}
            </Box>
            {supportDepts.length > 0 && (
              <TextField fullWidth multiline minRows={2}
                label="Notes for department (sent in the FYI email)"
                value={supportNotes}
                onChange={(e) => setSupportNotes(e.target.value)}
                sx={inputSx}
                helperText={`Will send one FYI email per department (${supportDepts.join(', ')}) at 6 PM, CC the PM + fieldops@.`} />
            )}
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: `${T.radiusSm}px` }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {isEdit && focusDateInRange && (
          <Button color="warning" startIcon={<DeleteIcon />} onClick={handleDeleteDay}
            sx={{ textTransform: 'none', fontWeight: 500 }}>
            {`Delete just ${(() => { try { return new Date(focusDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); } catch { return focusDate; } })()}`}
          </Button>
        )}
        {isEdit && (
          <Button color="error" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ textTransform: 'none', fontWeight: 500 }}>
            {spansMultipleDays ? 'Delete whole block' : 'Delete'}
          </Button>
        )}
        {isEdit && authority === 'confirmed' && existing?.status && existing.status !== 'confirmed' && (
          <Button
            variant="outlined"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirm}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, ml: 1 }}>
            Confirm
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, px: 2.5, boxShadow: 'none', '&:hover': { boxShadow: T.shadowSm } }}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Block')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Conflicts drawer (notification-style) ────────────────────────────────────
const CONFLICT_META = {
  double_book:      { label: 'Double-booked',          sev: 'high'   },
  cross_zone:       { label: 'Cross-zone same day',    sev: 'medium' },
  unconfirmed_late: { label: 'Not confirmed in time',  sev: 'medium' },
  weekend_no_oncall:{ label: 'No weekend on-call',     sev: 'high'   },
  pm_no_tasks:      { label: 'PM — no tasks logged',   sev: 'medium' },
};

const ConflictsDrawer = ({ open, onClose, conflicts, onJump }) => (
  <Drawer anchor="right" open={open} onClose={onClose}
    PaperProps={{ sx: { width: 420, bgcolor: T.surface, fontFamily: T.fontStack } }}>
    <Box sx={{ p: 3, borderBottom: `1px solid ${T.borderSoft}`, display: 'flex', alignItems: 'center' }}>
      <Typography sx={{ fontWeight: 600, fontSize: 16, color: T.text, flex: 1 }}>
        Conflicts <Typography component="span" sx={{ color: T.textMuted, fontWeight: 500, ml: 1 }}>({conflicts.length})</Typography>
      </Typography>
      <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
    </Box>
    {conflicts.length === 0 && (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: T.textMuted, fontSize: 14 }}>No conflicts this week.</Typography>
      </Box>
    )}
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {conflicts.map((c, i) => {
        const meta = CONFLICT_META[c.type] || { label: c.type, sev: 'medium' };
        const style = statusStyle(meta.sev === 'high' ? 'flagged' : 'suggested');
        return (
          <Box key={i}
            onClick={() => onJump && onJump(c)}
            sx={{
              p: 2, borderRadius: `${T.radiusSm}px`, cursor: c.blocks?.length ? 'pointer' : 'default',
              bgcolor: style.tint, border: `1px solid ${style.border}`,
              transition: T.transition,
              '&:hover': c.blocks?.length ? { boxShadow: T.shadowSm, transform: 'translateY(-1px)' } : undefined,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <StatusDot status={meta.sev === 'high' ? 'flagged' : 'suggested'} />
              <Typography sx={{ fontWeight: 600, fontSize: 12, color: style.text, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {meta.label}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {c.blocks?.length ? <ArrowOutwardIcon sx={{ fontSize: 14, color: style.text, opacity: 0.6 }} /> : null}
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.text, mb: 0.25 }}>
              {c.tech_name || c.summary || 'Issue'}
            </Typography>
            <Typography sx={{ fontSize: 13, color: T.textMuted }}>
              {c.date && new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {c.detail ? ` · ${c.detail}` : ''}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Drawer>
);

// ─── Main page ────────────────────────────────────────────────────────────────
// ─── Labor Request modal (create / fulfill) ─────────────────────────────────
// Two modes:
//   mode='create'  — PM / dept head creating a new request
//   mode='fulfill' — confirmed-authority user viewing and assigning techs
const DISCIPLINES = ['AV','Lighting','Shade','IT','Programming','CAD','Other'];

const emptyGroup = (req) => ({
  tech_ids: [],
  start_date: req?.start_date?.slice(0,10) || '',
  end_date:   req?.end_date?.slice(0,10)   || '',
  start_hour: req?.start_hour != null ? String(req.start_hour).padStart(2,'0') + ':00' : '',
  end_hour:   req?.end_hour   != null ? String(req.end_hour).padStart(2,'0')   + ':00' : '',
  report_to_location: 'job_site',
  report_to_note: '',
  task_notes: req?.notes || '',
  task_type:  req?.task_type || '',
  site_contact_name:  req?.site_contact_name  || '',
  site_contact_phone: req?.site_contact_phone || '',
  site_contact_email: req?.site_contact_email || '',
});

const LaborRequestModal = ({ open, mode, existing, techs, authority, me, onClose, onSaved }) => {
  const isFulfill = mode === 'fulfill';

  // ─── Create-mode state ────────────────────────────────────────────────
  const [discipline, setDiscipline] = useState('AV');
  const [techsNeeded, setTechsNeeded] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [startHour, setStartHour] = useState('');
  const [endHour, setEndHour]     = useState('');
  const [requiredBy, setRequiredBy] = useState('');
  const [addr, setAddr] = useState(null);
  const [taskType, setTaskType] = useState('');
  const [notes, setNotes] = useState('');
  const [preferredTechId, setPreferredTechId] = useState('');
  const [chargeType, setChargeType] = useState('job');
  const [createSiteContactName,  setCreateSiteContactName]  = useState('');
  const [createSiteContactPhone, setCreateSiteContactPhone] = useState('');
  const [createSiteContactEmail, setCreateSiteContactEmail] = useState('');

  // ─── Fulfill-mode state ───────────────────────────────────────────────
  const [groups, setGroups] = useState([]);
  const [declineReason, setDeclineReason] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null); setSaving(false); setDeclineReason('');
    if (isFulfill && existing) {
      // Fulfill mode: seed one group matching the request
      setGroups([emptyGroup(existing)]);
    } else {
      // Create mode defaults
      const today = new Date();
      const in3 = new Date(); in3.setDate(today.getDate() + 3);
      const in5 = new Date(); in5.setDate(today.getDate() + 5);
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      setDiscipline('AV'); setTechsNeeded(1);
      setStartDate(iso(in5)); setEndDate(iso(in5));
      setStartHour(''); setEndHour('');
      setRequiredBy(iso(in3));
      setAddr(null); setTaskType(''); setNotes(''); setPreferredTechId(''); setChargeType('job');
      setCreateSiteContactName(''); setCreateSiteContactPhone(''); setCreateSiteContactEmail('');
    }
  }, [open, isFulfill, existing]);

  const canConfirm = authority === 'confirmed';
  const alreadyFulfilled = existing?.fulfilled_count || 0;
  const stillNeeded = isFulfill && existing ? Math.max(0, (existing.techs_needed || 0) - alreadyFulfilled) : 0;
  const planAssigned = groups.reduce((n, g) => n + (g.tech_ids?.length || 0), 0);

  const updateGroup = (idx, patch) => {
    setGroups((prev) => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
  };
  const addGroup = () => setGroups((prev) => [...prev, emptyGroup(existing)]);
  const removeGroup = (idx) => setGroups((prev) => prev.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    setError(null);
    if (!discipline) return setError('Pick a discipline');
    if (!startDate || !endDate) return setError('Pick start/end dates');
    if (!requiredBy) return setError('Required-by date is required');
    if (!notes.trim()) return setError('Notes are required');
    if (!createSiteContactName.trim())  return setError('Primary site contact name is required');
    if (!createSiteContactPhone.trim()) return setError('Primary site contact phone is required');
    const payload = {
      discipline, techs_needed: Math.max(1, parseInt(techsNeeded,10) || 1),
      start_date: startDate, end_date: endDate,
      start_hour: startHour ? parseInt(startHour.split(':')[0], 10) : null,
      end_hour:   endHour   ? parseInt(endHour.split(':')[0],   10) : null,
      required_by: requiredBy,
      job_number: addr?.job_number || null, job_label: addr?.job_label || null,
      site_address: addr?.site_address || null, site_city: addr?.site_city || null,
      site_state: addr?.site_state || null, address_source: addr?.source || null,
      task_type: taskType || null, notes,
      preferred_tech_id: preferredTechId || null,
      charge_type: chargeType,
      site_contact_name:  createSiteContactName.trim(),
      site_contact_phone: createSiteContactPhone.trim(),
      site_contact_email: createSiteContactEmail.trim() || null,
    };
    setSaving(true);
    try {
      const t = localStorage.getItem('token');
      await axios.post('/api/dispatch-preview/labor-requests', payload, { headers: { Authorization: `Bearer ${t}` } });
      onSaved(); onClose();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleAssign = async () => {
    setError(null);
    // Client-side validation on groups
    for (const [i, g] of groups.entries()) {
      if (!g.tech_ids?.length) return setError(`Group ${i+1}: pick at least one tech`);
      if (!g.start_date || !g.end_date) return setError(`Group ${i+1}: dates required`);
      if (!g.site_contact_name?.trim())  return setError(`Group ${i+1}: site contact name required`);
      if (!g.site_contact_phone?.trim()) return setError(`Group ${i+1}: site contact phone required`);
    }
    if (planAssigned > stillNeeded) {
      return setError(`Over-staffing: ${stillNeeded} tech(s) still needed, you selected ${planAssigned}.`);
    }
    const payloadGroups = groups.map((g) => ({
      tech_ids: g.tech_ids,
      start_date: g.start_date, end_date: g.end_date,
      start_hour: g.start_hour ? parseInt(g.start_hour.split(':')[0], 10) : null,
      end_hour:   g.end_hour   ? parseInt(g.end_hour.split(':')[0],   10) : null,
      report_to_location: g.report_to_location || null,
      report_to_note: g.report_to_location === 'other' ? (g.report_to_note || null) : null,
      task_notes: g.task_notes || null,
      task_type:  g.task_type  || null,
      site_contact_name:  g.site_contact_name.trim(),
      site_contact_phone: g.site_contact_phone.trim(),
      site_contact_email: g.site_contact_email?.trim() || null,
    }));
    setSaving(true);
    try {
      const t = localStorage.getItem('token');
      await axios.post(`/api/dispatch-preview/labor-requests/${existing.id}/assign`,
        { groups: payloadGroups },
        { headers: { Authorization: `Bearer ${t}` } });
      onSaved(); onClose();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleDecline = async () => {
    setError(null);
    if (!declineReason.trim()) return setError('Reason is required to decline');
    setSaving(true);
    try {
      const t = localStorage.getItem('token');
      await axios.post(`/api/dispatch-preview/labor-requests/${existing.id}/decline`,
        { reason: declineReason },
        { headers: { Authorization: `Bearer ${t}` } });
      onSaved(); onClose();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!existing?.id) return;
    if (!window.confirm('Cancel this labor request?')) return;
    setSaving(true);
    try {
      const t = localStorage.getItem('token');
      await axios.post(`/api/dispatch-preview/labor-requests/${existing.id}/cancel`, {},
        { headers: { Authorization: `Bearer ${t}` } });
      onSaved(); onClose();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const inputSx = { '& .MuiInputBase-root': { borderRadius: `${T.radiusSm}px` } };

  // ─── Fulfill-mode render ────────────────────────────────────────────
  const renderFulfill = () => (
    <>
      {/* Request summary (read-only) */}
      <Box sx={{ p: 2, borderRadius: `${T.radiusSm}px`, bgcolor: '#F8FAFC', border: `1px solid ${T.border}`, mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Request from {existing?.requester_name}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, rowGap: 0.5, columnGap: 2, fontSize: 13 }}>
          <div><strong>Discipline:</strong> {existing?.discipline} × {existing?.techs_needed}</div>
          <div><strong>Required by:</strong> {existing?.required_by?.slice(0,10)}</div>
          <div><strong>Job:</strong> {existing?.job_label || existing?.job_number || '—'}</div>
          <div><strong>Site:</strong> {existing?.site_address ? `${existing.site_address}, ${existing.site_city || ''}` : '—'}</div>
          <div><strong>Dates:</strong> {existing?.start_date?.slice(0,10)}{existing?.start_date !== existing?.end_date && ` → ${existing?.end_date?.slice(0,10)}`}</div>
          <div><strong>Site contact (default):</strong> {existing?.site_contact_name} · {existing?.site_contact_phone}</div>
        </Box>
        <Typography sx={{ fontSize: 13, mt: 1, color: T.text }}>
          <strong>Notes:</strong> {existing?.notes}
        </Typography>
        <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 1 }}>
          {alreadyFulfilled} of {existing?.techs_needed} techs already staffed.
          {' '}Planning <strong style={{ color: planAssigned > stillNeeded ? '#B71C1C' : T.text }}>{planAssigned}</strong> more in this fulfillment.
        </Typography>
      </Box>

      {/* Fulfillment plan — list of groups */}
      {canConfirm && stillNeeded > 0 && groups.map((g, i) => (
        <Box key={i} sx={{ p: 2, borderRadius: `${T.radiusSm}px`, border: `1px solid ${T.border}`, bgcolor: T.surface, mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: T.text, flex: 1 }}>
              Assignment group #{i+1}
            </Typography>
            {groups.length > 1 && (
              <IconButton size="small" onClick={() => removeGroup(i)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Autocomplete multiple
              options={techs}
              getOptionLabel={(t) => `${t.name} — ${t.role}`}
              value={techs.filter((t) => g.tech_ids.includes(t.id))}
              onChange={(_, v) => updateGroup(i, { tech_ids: v.map((t) => t.id) })}
              renderInput={(params) => <TextField {...params} label="Techs *" sx={inputSx} />}
              sx={{ gridColumn: { sm: 'span 2' } }}
            />
            <TextField fullWidth type="date" label="Start date *" InputLabelProps={{ shrink: true }}
              value={g.start_date} onChange={(e) => updateGroup(i, { start_date: e.target.value })} sx={inputSx} />
            <TextField fullWidth type="date" label="End date *" InputLabelProps={{ shrink: true }}
              value={g.end_date} onChange={(e) => updateGroup(i, { end_date: e.target.value })} sx={inputSx} />
            <TextField fullWidth type="time" label="Start time" InputLabelProps={{ shrink: true }}
              value={g.start_hour} onChange={(e) => updateGroup(i, { start_hour: e.target.value })}
              inputProps={{ step: 1800 }} sx={inputSx} />
            <TextField fullWidth type="time" label="End time" InputLabelProps={{ shrink: true }}
              value={g.end_hour} onChange={(e) => updateGroup(i, { end_hour: e.target.value })}
              inputProps={{ step: 1800 }} sx={inputSx} />
            <TextField select fullWidth label="Report-to location"
              value={g.report_to_location}
              onChange={(e) => updateGroup(i, { report_to_location: e.target.value })} sx={inputSx}>
              <MenuItem value="job_site">Job Site</MenuItem>
              <MenuItem value="acs_westbury">ACS Westbury</MenuItem>
              <MenuItem value="acs_nyc">ACS NYC</MenuItem>
              <MenuItem value="other">Other — specify</MenuItem>
            </TextField>
            {g.report_to_location === 'other' && (
              <TextField fullWidth label="Specify other location"
                value={g.report_to_note}
                onChange={(e) => updateGroup(i, { report_to_note: e.target.value })} sx={inputSx} />
            )}
            <TextField fullWidth label="Task type"
              value={g.task_type}
              onChange={(e) => updateGroup(i, { task_type: e.target.value })} sx={inputSx} />
          </Box>

          {/* Site contact per group (defaults from request, editable) */}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, mt: 2, mb: 1 }}>
            Site contact for this group
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField fullWidth required label="Name *"
              value={g.site_contact_name}
              onChange={(e) => updateGroup(i, { site_contact_name: e.target.value })}
              error={!g.site_contact_name?.trim()} sx={inputSx} />
            <TextField fullWidth required label="Phone *"
              value={g.site_contact_phone}
              onChange={(e) => updateGroup(i, { site_contact_phone: e.target.value })}
              error={!g.site_contact_phone?.trim()} sx={inputSx} />
            <TextField fullWidth label="Email (optional)"
              value={g.site_contact_email}
              onChange={(e) => updateGroup(i, { site_contact_email: e.target.value })} sx={inputSx} />
          </Box>

          <TextField fullWidth multiline minRows={2} label="Notes for this group (defaults from request)"
            value={g.task_notes}
            onChange={(e) => updateGroup(i, { task_notes: e.target.value })} sx={{ ...inputSx, mt: 2 }} />
        </Box>
      ))}

      {canConfirm && stillNeeded > 0 && (
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addGroup}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, mb: 2 }}>
          Add another assignment group
        </Button>
      )}

      {canConfirm && ['pending','partially_fulfilled'].includes(existing?.status) && (
        <Box sx={{ mt: 2 }}>
          <TextField fullWidth size="small" label="Decline reason (if declining)"
            value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} sx={inputSx} />
        </Box>
      )}
    </>
  );

  // ─── Create-mode render ─────────────────────────────────────────────
  const renderCreate = () => (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField select fullWidth label="Discipline *" value={discipline}
          onChange={(e) => setDiscipline(e.target.value)} sx={inputSx}>
          {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
        <TextField fullWidth type="number" label="# of techs needed *"
          value={techsNeeded} onChange={(e) => setTechsNeeded(e.target.value)}
          inputProps={{ min: 1, max: 20 }} sx={inputSx} />
        <TextField fullWidth type="date" label="Start date *" InputLabelProps={{ shrink: true }}
          value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={inputSx} />
        <TextField fullWidth type="date" label="End date *" InputLabelProps={{ shrink: true }}
          value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={inputSx} />
        <TextField fullWidth type="time" label="Start time" InputLabelProps={{ shrink: true }}
          value={startHour} onChange={(e) => setStartHour(e.target.value)}
          inputProps={{ step: 1800 }} sx={inputSx} />
        <TextField fullWidth type="time" label="End time" InputLabelProps={{ shrink: true }}
          value={endHour} onChange={(e) => setEndHour(e.target.value)}
          inputProps={{ step: 1800 }} sx={inputSx} />
        <TextField fullWidth type="date" label="Required by *" InputLabelProps={{ shrink: true }}
          value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} sx={inputSx}
          helperText="Hard deadline for staffing this request" />
        <TextField select fullWidth label="Charge type" value={chargeType}
          onChange={(e) => setChargeType(e.target.value)} sx={inputSx}>
          <MenuItem value="job">Job</MenuItem>
          <MenuItem value="service">Service</MenuItem>
          <MenuItem value="warranty">Warranty</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ mt: 2 }}>
        <AddressPicker value={addr} onChange={setAddr} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField fullWidth label="Task type" value={taskType}
          onChange={(e) => setTaskType(e.target.value)} sx={inputSx}
          placeholder="Rough-in, Service Call, Trim-out, …" />
      </Box>

      {/* Primary site contact */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, mt: 3, mb: 1 }}>
        Primary Site Contact
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
        <TextField fullWidth required label="Contact name *"
          value={createSiteContactName} onChange={(e) => setCreateSiteContactName(e.target.value)}
          error={!createSiteContactName.trim()}
          helperText={!createSiteContactName.trim() ? 'Required' : ' '}
          sx={inputSx} />
        <TextField fullWidth required label="Phone *"
          value={createSiteContactPhone} onChange={(e) => setCreateSiteContactPhone(e.target.value)}
          error={!createSiteContactPhone.trim()}
          helperText={!createSiteContactPhone.trim() ? 'Required' : ' '}
          placeholder="(555) 123-4567" sx={inputSx} />
        <TextField fullWidth label="Email (optional)"
          value={createSiteContactEmail} onChange={(e) => setCreateSiteContactEmail(e.target.value)}
          sx={inputSx} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField fullWidth multiline minRows={3} label="Notes *"
          required
          error={!notes.trim()}
          helperText={!notes.trim() ? 'Notes are required' : ' '}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={inputSx}
          placeholder="What needs doing, scope, access notes, special tools…" />
      </Box>

      <Box sx={{ mt: 2 }}>
        <TextField select fullWidth label="Preferred tech (optional)"
          value={preferredTechId}
          onChange={(e) => setPreferredTechId(e.target.value)} sx={inputSx}>
          <MenuItem value=""><em>No preference</em></MenuItem>
          {techs.map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name} — {t.role}</MenuItem>
          ))}
        </TextField>
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: `${T.radius}px`, fontFamily: T.fontStack } }}>
      <DialogTitle sx={{ fontWeight: 600, color: T.text, pb: 1.5 }}>
        {isFulfill ? `Fulfill Labor Request #${existing?.id}` : 'Request Labor'}
        {isFulfill && existing && (
          <Typography component="span" sx={{ ml: 2, fontSize: 13, color: T.textMuted, fontWeight: 500 }}>
            status: {existing.status}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {isFulfill ? renderFulfill() : renderCreate()}
        {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: `${T.radiusSm}px` }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {isFulfill && existing && Number(existing.requester_id) === Number(me?.user?.id || me?.id) &&
          ['pending','partially_fulfilled'].includes(existing.status) && (
          <Button color="error" onClick={handleCancel} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel request
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
        {!isFulfill && (
          <Button variant="contained" onClick={handleCreate} disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, px: 2.5, bgcolor: T.accent }}>
            {saving ? 'Submitting…' : 'Submit Request'}
          </Button>
        )}
        {isFulfill && canConfirm && ['pending','partially_fulfilled'].includes(existing?.status) && (
          <>
            <Button color="error" variant="outlined" onClick={handleDecline} disabled={saving || !declineReason.trim()}
              sx={{ textTransform: 'none', borderRadius: `${T.radiusSm}px` }}>
              Decline
            </Button>
            <Button variant="contained" onClick={handleAssign} disabled={saving || planAssigned === 0 || planAssigned > stillNeeded}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, px: 2.5, bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' } }}>
              {saving ? 'Assigning…' : `Assign ${planAssigned || ''}`.trim()}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const LaborRequestsDrawer = ({ open, onClose, requests, onOpenRequest, onCreateNew }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? requests : requests.filter((r) => ['pending','partially_fulfilled'].includes(r.status));
  const statusColor = (s) =>
    s === 'pending'              ? { bg: '#FFF4E5', bd: '#FFB74D', fg: '#B26A00' } :
    s === 'partially_fulfilled'  ? { bg: '#FFF9E6', bd: '#F9D71C', fg: '#8B6900' } :
    s === 'fulfilled'            ? { bg: '#F1F8F2', bd: '#A5D6A7', fg: '#1B5E20' } :
    s === 'declined'             ? { bg: '#FFEBEE', bd: '#EF9A9A', fg: '#B71C1C' } :
    s === 'cancelled'            ? { bg: '#F5F5F5', bd: '#E0E0E0', fg: '#616161' } :
                                    { bg: T.borderSoft, bd: T.border, fg: T.textMuted };
  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: 460, bgcolor: T.surface, fontFamily: T.fontStack } }}>
      <Box sx={{ p: 3, borderBottom: `1px solid ${T.borderSoft}`, display: 'flex', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16, color: T.text, flex: 1 }}>
          Labor Requests <Typography component="span" sx={{ color: T.textMuted, fontWeight: 500, ml: 1 }}>({visible.length})</Typography>
        </Typography>
        <Button size="small" onClick={() => setShowAll(!showAll)} sx={{ textTransform: 'none', fontSize: 12, mr: 1 }}>
          {showAll ? 'Pending only' : 'Show all'}
        </Button>
        <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${T.borderSoft}` }}>
        <Button fullWidth variant="outlined" startIcon={<AddIcon />} onClick={onCreateNew}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px` }}>
          New Request
        </Button>
      </Box>
      {visible.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: T.textMuted, fontSize: 14 }}>
            {showAll ? 'No requests yet.' : 'No pending requests.'}
          </Typography>
        </Box>
      )}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visible.map((r) => {
          const c = statusColor(r.status);
          const overdueDays = r.required_by
            ? Math.ceil((new Date(r.required_by + 'T00:00:00') - new Date()) / 86400000)
            : null;
          return (
            <Box key={r.id} onClick={() => onOpenRequest(r)}
              sx={{ p: 2, borderRadius: `${T.radiusSm}px`, cursor: 'pointer',
                bgcolor: c.bg, border: `1px solid ${c.bd}`,
                transition: T.transition,
                '&:hover': { boxShadow: T.shadowSm, transform: 'translateY(-1px)' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 12, color: c.fg, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {r.status.replace('_',' ')}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11, color: T.textMuted }}>#{r.id}</Typography>
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.text, mb: 0.25 }}>
                {r.discipline} × {r.techs_needed}
                {r.techs_needed > 1 && ` — ${r.fulfilled_count || 0} staffed`}
              </Typography>
              <Typography sx={{ fontSize: 13, color: T.text }}>
                {r.job_label || r.job_number || '(no job)'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 0.25 }}>
                {r.requester_name} · {r.start_date?.slice(0,10)}
                {r.start_date !== r.end_date && ` → ${r.end_date?.slice(0,10)}`}
                {' · by '}{r.required_by?.slice(0,10)}
                {overdueDays != null && overdueDays <= 1 && ['pending','partially_fulfilled'].includes(r.status) && (
                  <Typography component="span" sx={{ color: '#B71C1C', fontWeight: 600, ml: 0.5 }}>
                    {overdueDays < 0 ? ` (${Math.abs(overdueDays)}d overdue)` : overdueDays === 0 ? ' (today!)' : ' (tomorrow)'}
                  </Typography>
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Drawer>
  );
};

// ─── Read-only block detail dialog ───────────────────────────────────────────
// Shown when a user WITHOUT edit authority (techs, viewers) clicks a dispatch
// block. Non-editors previously only got the hover tooltip, which vanished on
// mouse-out and offered no way to copy, scroll, or share. This gives them a
// persistent dialog with the same information in a selectable, scrollable
// form. (Lester Jacquett 2026-11-03.)
const BlockDetailDialog = ({ open, block, onClose }) => {
  if (!block) return null;
  const reportLabel =
    block.report_to_location === 'job_site'     ? 'Job Site'      :
    block.report_to_location === 'acs_westbury' ? 'ACS Westbury'  :
    block.report_to_location === 'acs_nyc'      ? 'ACS NYC'       :
    (block.report_to_note || block.report_to_location || null);
  const timeStr = (block.start_hour != null)
    ? `${String(block.start_hour).padStart(2,'0')}:00${block.end_hour != null ? `–${String(block.end_hour).padStart(2,'0')}:00` : ''}`
    : null;
  const addr = [block.site_address, [block.site_city, block.site_state].filter(Boolean).join(', ')]
    .filter(Boolean).join(' · ');
  const Row = ({ label, value, mono = false, multiline = false }) => {
    if (value == null || value === '') return null;
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' }, gap: { xs: 0.5, sm: 2 }, py: 1.25, borderBottom: `1px solid ${T.borderSoft}` }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.textFaint, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 14, color: T.text,
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          wordBreak: 'break-word',
          userSelect: 'text',
        }}>
          {value}
        </Typography>
      </Box>
    );
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: `${T.radius}px`, fontFamily: T.fontStack } }}>
      <DialogTitle sx={{ pr: 6, pb: 1.5, borderBottom: `1px solid ${T.borderSoft}` }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>
          {block.job_label || block.task_type || 'Dispatch block'}
        </Typography>
        {block.job_number && (
          <Typography sx={{ fontSize: 12, color: T.textMuted, fontFamily: 'ui-monospace, SFMono-Regular, monospace', mt: 0.5 }}>
            {block.job_number}
          </Typography>
        )}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 1, px: { xs: 2, sm: 3 } }}>
        <Row label="Tech" value={block.tech_name} />
        <Row label="When" value={timeStr} />
        <Row label="Site" value={addr} />
        <Row label="Report to" value={reportLabel} />
        <Row label="Task" value={block.task_type} />
        <Row label="Notes" value={block.task_notes} multiline />
        <Row label="Status" value={block.status} />
        <Row label="PM" value={block.pm_name} />
        <Row label="Scheduled by" value={block.created_by_name} />
        {Array.isArray(block.support_departments) && block.support_departments.length > 0 && (
          <Row label="Dept support" value={block.support_departments.join(', ')} />
        )}
        {block.support_notes && <Row label="Dept notes" value={block.support_notes} multiline />}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${T.borderSoft}` }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px` }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DispatchPreview = () => {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [me, setMe]               = useState(null);
  const [techs, setTechs]         = useState([]);
  const [techGroups, setTechGroups] = useState([]);
  const [pms, setPms]             = useState([]);
  const [library, setLibrary]     = useState([]);
  const [blocks, setBlocks]       = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [conflictsOpen, setConflictsOpen] = useState(false);
  // Day-close review flow (Andrew Witanowski, confirmed-authority only).
  // Tom Clancy 2026-11-02 — per-day scope: flags CONFIRMED assignments that
  // have no task_type and no task_notes on the chosen date, emails each PM.
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeDate, setCloseDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [closePreview, setClosePreview] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState(null);
  const [closeResult, setCloseResult] = useState(null);

  const loadClosePreview = useCallback(async (date) => {
    setCloseError(null); setClosePreview(null); setCloseResult(null);
    try {
      const t = localStorage.getItem('token');
      const r = await axios.get('/api/dispatch-preview/close-day/preview',
        { headers: { Authorization: `Bearer ${t}` }, params: { date } });
      setClosePreview(r.data);
    } catch (e) {
      setCloseError(e.response?.data?.error || e.message);
    }
  }, []);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDefaults, setModalDefaults] = useState(null);
  const [modalExisting, setModalExisting] = useState(null);
  const [modalFocusDate, setModalFocusDate] = useState(null);
  // Read-only block detail dialog (for techs / anyone without edit authority).
  // Previously a non-editor clicking a block did nothing — only the hover tooltip
  // showed detail, which vanished on mouse-out and couldn't be copied from.
  // Reported by Lester Jacquett 2026-11-03.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBlock, setDetailBlock] = useState(null);
  const [highlightBlockId, setHighlightBlockId] = useState(null);
  const [laborRequests, setLaborRequests] = useState([]);
  const [requestsDrawerOpen, setRequestsDrawerOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestModalMode, setRequestModalMode] = useState('create');
  const [requestModalExisting, setRequestModalExisting] = useState(null);

  const days    = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const today   = useMemo(() => new Date(), []);

  const authority = me?.authority || 'read';
  const canEdit = authority !== 'read';

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    const t = token();
    const h = { Authorization: `Bearer ${t}` };
    Promise.all([
      axios.get('/api/dispatch-preview/me', { headers: h }),
      axios.get('/api/dispatch-preview/techs', { headers: h }),
      axios.get('/api/dispatch-preview/pms', { headers: h }),
      axios.get('/api/dispatch-preview/task-library', { headers: h }),
    ]).then(([m, t2, p, l]) => {
      setMe(m.data);
      setTechs(t2.data?.techs || []);
      setTechGroups(t2.data?.groups || []);
      setPms(p.data?.pms || []);
      setLibrary(l.data?.library || []);
    }).catch((e) => setError(e.response?.data?.error || e.message));
  }, []);

  const refreshBlocks = useCallback(() => {
    setLoading(true);
    axios.get('/api/dispatch-preview/blocks', {
      headers: { Authorization: `Bearer ${token()}` },
      params: { from: ymd(weekStart), to: ymd(weekEnd) },
    })
      .then((r) => setBlocks(r.data?.blocks || []))
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [weekStart, weekEnd]);

  const refreshConflicts = useCallback(() => {
    axios.get('/api/dispatch-preview/conflicts', {
      headers: { Authorization: `Bearer ${token()}` },
      params: { week: ymd(weekStart) },
    })
      .then((r) => {
        // Guard: if the endpoint fell through to the SPA (returns HTML),
        // r.data is a string, not an object. Only accept arrays.
        const list = Array.isArray(r.data?.conflicts) ? r.data.conflicts : [];
        setConflicts(list);
      })
      .catch(() => setConflicts([]));
  }, [weekStart]);

  useEffect(() => { refreshBlocks(); refreshConflicts(); }, [refreshBlocks, refreshConflicts]);

  const refreshLaborRequests = useCallback(() => {
    axios.get('/api/dispatch-preview/labor-requests', {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then((r) => setLaborRequests(Array.isArray(r.data?.requests) ? r.data.requests : []))
      .catch(() => setLaborRequests([]));
  }, []);

  useEffect(() => { refreshLaborRequests(); }, [refreshLaborRequests]);

  // Poll conflicts + labor requests every 60s
  useEffect(() => {
    const id = setInterval(() => { refreshConflicts(); refreshLaborRequests(); }, 60000);
    return () => clearInterval(id);
  }, [refreshConflicts, refreshLaborRequests]);

  const blocksByTechDay = useMemo(() => {
    const m = {};
    for (const b of blocks) {
      const sd = new Date(b.start_date);
      const ed = new Date(b.end_date);
      for (let d = new Date(sd); d <= ed; d.setDate(d.getDate() + 1)) {
        const key = `${b.tech_id}|${ymd(d)}`;
        (m[key] = m[key] || []).push(b);
      }
    }
    return m;
  }, [blocks]);

  // Conflict index for fast badge lookup
  const conflictsByTechDay = useMemo(() => {
    const m = {};
    for (const c of conflicts) {
      if (!c.tech_id || !c.date) continue;
      const key = `${c.tech_id}|${c.date}`;
      (m[key] = m[key] || []).push(c);
    }
    return m;
  }, [conflicts]);

  const conflictsByTech = useMemo(() => {
    const m = {};
    for (const c of conflicts) {
      if (!c.tech_id) continue;
      m[c.tech_id] = (m[c.tech_id] || 0) + 1;
    }
    return m;
  }, [conflicts]);

  const openCreate = (defaults) => {
    if (!canEdit) return;
    setModalExisting(null);
    setModalDefaults(defaults);
    setModalFocusDate(null);
    setModalOpen(true);
  };
  const openEdit = (block, cellDate = null) => {
    if (!canEdit) {
      // Read-only users (techs, etc.) get a persistent detail dialog they can
      // read, scroll, and copy text from — instead of a disappearing hover tip.
      setDetailBlock(block);
      setDetailOpen(true);
      return;
    }
    setModalExisting(block);
    setModalDefaults(null);
    setModalFocusDate(cellDate);
    setModalOpen(true);
  };

  const jumpToConflict = (c) => {
    if (!c.blocks?.length) return;
    const bid = c.blocks[0];
    setHighlightBlockId(bid);
    setConflictsOpen(false);
    setTimeout(() => {
      const el = document.querySelector(`[data-block-id="${bid}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    setTimeout(() => setHighlightBlockId(null), 2200);
  };

  const authorityBadge = (() => {
    if (authority === 'confirmed') {
      const s = S.confirmed;
      return <Chip size="small" label="Confirmed authority" sx={{ bgcolor: s.tint, color: s.text, fontWeight: 600, border: `1px solid ${s.border}`, borderRadius: 1.5 }} />;
    }
    if (authority === 'suggest') {
      const s = S.suggested;
      return <Chip size="small" label="Suggest authority" sx={{ bgcolor: s.tint, color: s.text, fontWeight: 600, border: `1px solid ${s.border}`, borderRadius: 1.5 }} />;
    }
    return <Chip size="small" label="Read-only" variant="outlined" sx={{ borderRadius: 1.5, color: T.textMuted }} />;
  })();

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: T.fontStack, bgcolor: T.bg, minHeight: '100vh', color: T.text }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px', color: T.text, lineHeight: 1.1 }}>
            Dispatch
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {authorityBadge}
          {authority === 'confirmed' && (
            <Tooltip title="Close a day — flag any CONFIRMED blocks missing task/notes and email the PMs">
              <Button
                variant="outlined"
                size="small"
                startIcon={<StickyNote2Icon fontSize="small" />}
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setCloseDate(today);
                  setCloseOpen(true);
                  loadClosePreview(today);
                }}
                sx={{ borderRadius: `${T.radiusSm}px`, textTransform: 'none', fontWeight: 600 }}
              >
                Close Day
              </Button>
            </Tooltip>
          )}
          <Tooltip title={`${conflicts.length} conflict${conflicts.length === 1 ? '' : 's'} this week`}>
            <Badge color="error" badgeContent={conflicts.length} invisible={conflicts.length === 0}
              sx={{ '& .MuiBadge-badge': { fontWeight: 700, boxShadow: `0 0 0 2px ${T.bg}` } }}>
              <IconButton onClick={() => setConflictsOpen(true)}
                sx={{ bgcolor: T.surface, border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px`,
                  transition: T.transition, '&:hover': { bgcolor: T.surface, boxShadow: T.shadowSm } }}>
                <NotificationsIcon fontSize="small" sx={{ color: conflicts.length ? S.flagged.dot : T.textMuted }} />
              </IconButton>
            </Badge>
          </Tooltip>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${T.radiusSm}px` }}>{error}</Alert>}

      {/* Toolbar card */}
      <Paper elevation={0} sx={{
        p: 2, mb: 2.5, borderRadius: `${T.radius}px`,
        bgcolor: T.surface, border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, -7))} size="small"
          sx={{ border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px`, transition: T.transition,
            '&:hover': { bgcolor: T.borderSoft } }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => setWeekStart(addDays(weekStart, 7))} size="small"
          sx={{ border: `1px solid ${T.border}`, borderRadius: `${T.radiusSm}px`, transition: T.transition,
            '&:hover': { bgcolor: T.borderSoft } }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Button onClick={() => setWeekStart(startOfWeek(new Date()))}
          sx={{ textTransform: 'none', fontWeight: 500, color: T.textMuted, borderRadius: `${T.radiusSm}px` }}>
          Today
        </Button>
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: T.text, ml: 1 }}>
          {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          {' – '}
          {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button variant="text" onClick={() => navigate('/dispatch-template/on-call')}
            sx={{ textTransform: 'none', fontWeight: 500, color: T.textMuted, borderRadius: `${T.radiusSm}px`,
              '&:hover': { bgcolor: T.borderSoft, color: T.text } }}>
            On-Call Rotation
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit}
            onClick={() => openCreate({ date: ymd(new Date()) })}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, boxShadow: 'none', px: 2,
              bgcolor: T.accent, '&:hover': { bgcolor: '#15263F', boxShadow: T.shadowSm } }}>
            New Assignment
          </Button>
          <Badge badgeContent={laborRequests.filter(r => ['pending','partially_fulfilled'].includes(r.status)).length}
                 color="warning" overlap="rectangular"
                 sx={{ '& .MuiBadge-badge': { fontWeight: 700, right: 6, top: 6 } }}>
            <Button variant="outlined" disabled={!canEdit}
              onClick={() => setRequestsDrawerOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: `${T.radiusSm}px`, px: 2 }}>
              Request Labor
            </Button>
          </Badge>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1, height: 2 }} />}

      {/* Calendar surface */}
      {/* Mobile fix (Tom 2026-11-03): the Nov 2 approach relied on window-level
          horizontal scroll (body overflow-x: clip + grid min-width: 1100). That
          works on desktop/laptop but on iPhone the body clips the overflow and
          there's no way to pan to see Wed–Sun of the week. Fix: on narrow
          viewports (<= 900px ≈ iPhone/landscape phone/narrow tablet) give the
          grid its own horizontal scroll container. The known tradeoff per the
          Nov 2 comment is that mixing overflow-x:auto with overflow-y:visible
          promotes Y to auto and breaks window-level sticky — so on mobile we
          accept a non-sticky day header (mobile users rarely scroll through
          many department sections at once). On desktop (>900px) the Nov 2
          behavior is preserved so the sticky day header still pins under the
          AppBar. */}
      <Paper elevation={0} sx={{
        borderRadius: `${T.radius}px`, bgcolor: T.surface,
        border: `1px solid ${T.border}`, boxShadow: T.shadowMd,
        overflow: { xs: 'hidden', md: 'visible' },
      }}>
        <Box sx={{
          overflowX: { xs: 'auto', md: 'visible' },
          overflowY: { xs: 'hidden', md: 'visible' },
          WebkitOverflowScrolling: 'touch',
        }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: `240px repeat(7, minmax(140px, 1fr))`, minWidth: 1100 }}>
            {/* Header row — sticky so the day banner stays pinned under the AppBar
                while the user scrolls through department sections. top: 64 clears
                the fixed MUI AppBar. (Tom 2026-11-02) */}
            <Box sx={{
              p: 2, bgcolor: T.surface, borderBottom: `1px solid ${T.borderSoft}`,
              position: 'sticky', top: 64, left: 0, zIndex: 4,
            }}>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.textFaint, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Technician
              </Typography>
            </Box>
            {days.map((d) => {
              const todayCell = isSameDay(d, today);
              return (
                <Box key={ymd(d)} sx={{
                  p: 2, textAlign: 'center', borderBottom: `1px solid ${T.borderSoft}`,
                  borderLeft: `1px solid ${T.borderSoft}`, bgcolor: T.surface,
                  position: 'sticky', top: 64, zIndex: 3,
                }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: T.textFaint, letterSpacing: 0.8 }}>
                    {fmtDay(d)}
                  </Typography>
                  <Box sx={{ mt: 0.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 32, height: 32, borderRadius: '999px',
                    bgcolor: todayCell ? T.accent : 'transparent',
                    color: todayCell ? '#fff' : (isWeekend(d) ? T.textFaint : T.text),
                    fontWeight: 600, fontSize: 14, px: 1,
                    boxShadow: todayCell ? T.shadowSm : 'none',
                  }}>
                    {d.getDate()}
                  </Box>
                </Box>
              );
            })}

            {/* Tech rows — grouped by department: AfterCare → Installation → Lighting & Shades → IT → Programming → PMs → CAD */}
            {(techGroups.length > 0 ? techGroups : [{ name: '', color: T.textMuted, techs }]).map((group, gIdx) => (
              <React.Fragment key={group.name || 'all'}>
                {group.name && (
                  <Box sx={{
                    gridColumn: '1 / span 8',
                    px: 2.5, pt: gIdx === 0 ? 1.5 : 2.5, pb: 1,
                    bgcolor: T.bg,
                    borderBottom: `1px solid ${T.borderSoft}`,
                    borderTop: gIdx === 0 ? 'none' : `1px solid ${T.borderSoft}`,
                    display: 'flex', alignItems: 'center', gap: 1.25,
                  }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: group.color, flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: 11, fontWeight: 700, color: T.textMuted,
                      textTransform: 'uppercase', letterSpacing: 0.9,
                    }}>
                      {group.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 500, color: T.textFaint, ml: 0.5 }}>
                      · {group.techs.length}
                    </Typography>
                  </Box>
                )}
                {group.techs.map((tech, idx) => {
                  const techConflictCount = conflictsByTech[tech.id] || 0;
                  const stripe = idx % 2 === 1;
                  return (
                    <React.Fragment key={tech.id}>
                  <Box sx={{
                    p: 2, borderBottom: `1px solid ${T.borderSoft}`,
                    bgcolor: stripe ? '#FBFCFE' : T.surface,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                  }}>
                    <AvatarChip name={tech.name} size={34} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {tech.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: T.textMuted, mt: 0.25 }}>{tech.role}</Typography>
                    </Box>
                    {techConflictCount > 0 && (
                      <Tooltip title={`${techConflictCount} conflict${techConflictCount === 1 ? '' : 's'}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5,
                          px: 0.75, py: 0.25, borderRadius: 1, bgcolor: S.flagged.tint,
                          border: `1px solid ${S.flagged.border}` }}>
                          <StatusDot status="flagged" size={6} />
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: S.flagged.text }}>
                            {techConflictCount}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  {days.map((d) => {
                    const key = `${tech.id}|${ymd(d)}`;
                    const dayBlocks = blocksByTechDay[key] || [];
                    const hasConflict = (conflictsByTechDay[key] || []).length > 0;
                    const todayCell = isSameDay(d, today);
                    return (
                      <Box
                        key={key}
                        onDoubleClick={() => openCreate({ techId: tech.id, date: ymd(d) })}
                        sx={{
                          position: 'relative',
                          p: 1, minHeight: 86,
                          borderBottom: `1px solid ${T.borderSoft}`, borderLeft: `1px solid ${T.borderSoft}`,
                          bgcolor: stripe ? '#FBFCFE' : T.surface,
                          display: 'flex', flexDirection: 'column', gap: 0.75,
                          cursor: canEdit ? 'pointer' : 'default',
                          transition: T.transition,
                          '&:hover': canEdit ? { bgcolor: '#F1F5FB' } : undefined,
                          ...(todayCell && { boxShadow: `inset 0 2px 0 ${T.accent}0A` }),
                        }}
                      >
                        {hasConflict && (
                          <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                            <StatusDot status="flagged" size={7} />
                          </Box>
                        )}
                        {dayBlocks.length === 0 ? (
                          canEdit ? (
                            <Box sx={{
                              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: `1px dashed ${T.borderSoft}`, borderRadius: `${T.radiusSm}px`,
                              fontSize: 11, color: T.textFaint, opacity: 0,
                              transition: T.transition,
                              'div:hover > &': { opacity: 1 },
                            }}>
                              + Add
                            </Box>
                          ) : null
                        ) : (
                          dayBlocks.map((b) => {
                            const s = statusStyle(b.status);
                            const highlighted = highlightBlockId === b.id;
                            // ── Non-assignment block kinds (Mike Grady 10/27) ──
                            if (b.block_kind === 'time_off') {
                              const portionLabel = b.off_portion === 'am' ? 'Morning' : b.off_portion === 'pm' ? 'Afternoon' : 'All day';
                              return (
                                <Box key={b.id} data-block-id={b.id}
                                  onClick={(e) => { e.stopPropagation(); if (canEdit) openEdit(b, ymd(d)); }}
                                  sx={{ px: 1, py: 0.75, borderRadius: `${T.radiusSm}px`,
                                    bgcolor: '#F1F5F9', border: '1px dashed #94A3B8',
                                    backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(148,163,184,0.18) 6px 7px)',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    boxShadow: highlighted ? `0 0 0 2px #64748B, ${T.shadowMd}` : 'none' }}>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#334155', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                    OFF — {portionLabel}
                                  </Typography>
                                  {b.task_notes && (
                                    <Typography sx={{ fontSize: 10.5, color: '#475569', mt: 0.25, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {b.task_notes}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            }
                            if (b.block_kind === 'holiday') {
                              return (
                                <Box key={b.id} data-block-id={b.id}
                                  onClick={(e) => { e.stopPropagation(); if (canEdit) openEdit(b, ymd(d)); }}
                                  sx={{ px: 1, py: 0.75, borderRadius: `${T.radiusSm}px`,
                                    bgcolor: '#FFF4E5', border: '1px solid #FFB300',
                                    cursor: canEdit ? 'pointer' : 'default', textAlign: 'center',
                                    boxShadow: highlighted ? `0 0 0 2px #FFB300, ${T.shadowMd}` : 'none' }}>
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#8B5A00', letterSpacing: 0.3, textTransform: 'uppercase' }}>
                                    HOLIDAY{b.job_label ? ` — ${b.job_label}` : ''}
                                  </Typography>
                                </Box>
                              );
                            }
                            if (b.block_kind === 'note') {
                              return (
                                <Box key={b.id} data-block-id={b.id}
                                  onClick={(e) => { e.stopPropagation(); if (canEdit) openEdit(b, ymd(d)); }}
                                  sx={{ px: 1, py: 0.5, borderRadius: `${T.radiusSm}px`,
                                    bgcolor: '#FFFDE7', border: '1px solid #F9D71C',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    boxShadow: highlighted ? `0 0 0 2px #F9D71C, ${T.shadowMd}` : 'none' }}>
                                  <Typography sx={{ fontSize: 11, color: '#5F4B00', fontStyle: 'italic', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                    📝 {b.task_notes || 'Note'}
                                  </Typography>
                                </Box>
                              );
                            }
                            return (
                              <Tooltip key={b.id} placement="top" arrow title={
                                <Box sx={{ fontFamily: T.fontStack }}>
                                  <div style={{ fontWeight: 600 }}>{b.job_label || 'Untitled'}</div>
                                  {b.site_address && <div>{b.site_address}{b.site_city ? `, ${b.site_city}` : ''}</div>}
                                  {b.report_to_location && (
                                    <div>Report to: {
                                      b.report_to_location === 'job_site'     ? 'Job Site'      :
                                      b.report_to_location === 'acs_westbury' ? 'ACS Westbury'  :
                                      b.report_to_location === 'acs_nyc'      ? 'ACS NYC'       :
                                      (b.report_to_note || 'Other')
                                    }</div>
                                  )}
                                  {b.task_type && <div>Task: {b.task_type}</div>}
                                  {b.task_notes && <div>Notes: {b.task_notes}</div>}
                                  <div>Status: {b.status}{b.pm_name ? ` · PM: ${b.pm_name}` : ''}</div>
                                  <div>By: {b.created_by_name}</div>
                                </Box>
                              }>
                                <Box
                                  data-block-id={b.id}
                                  onClick={(e) => { e.stopPropagation(); openEdit(b, ymd(d)); }}
                                  sx={{
                                    px: 1, py: 0.75, borderRadius: `${T.radiusSm}px`,
                                    bgcolor: b.status === 'flagged' ? s.tint : T.surface,
                                    border: `1px solid ${s.border}`,
                                    borderLeft: `3px solid ${s.dot}`,
                                    cursor: 'pointer', // always clickable: editors edit, non-editors see detail dialog (Lester 2026-11-03)
                                    transition: T.transition,
                                    boxShadow: highlighted ? `0 0 0 2px ${s.dot}, ${T.shadowMd}` : 'none',
                                    '&:hover': { boxShadow: T.shadowSm, transform: 'translateY(-1px)' },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <StatusDot status={b.status} />
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.text,
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                      {b.job_label || b.task_type || 'Block'}
                                    </Typography>
                                  </Box>
                                  {(b.start_hour != null || b.site_address) && (
                                    <Typography sx={{ fontSize: 10.5, color: T.textMuted, mt: 0.25,
                                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {b.start_hour != null && (
                                        <>
                                          {String(b.start_hour).padStart(2,'0')}:00
                                          {b.end_hour != null ? `–${String(b.end_hour).padStart(2,'0')}:00` : ''}
                                          {b.site_city || b.site_address ? ' · ' : ''}
                                        </>
                                      )}
                                      {b.site_city || b.site_address}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                    {b.task_type && (
                                      <Box sx={{ px: 0.75, py: 0.1, bgcolor: T.borderSoft, color: T.textMuted,
                                        fontSize: 10, fontWeight: 600, borderRadius: 1 }}>
                                        {b.task_type}
                                      </Box>
                                    )}
                                    {b.pm_name && (
                                      <Box sx={{ px: 0.75, py: 0.1, bgcolor: '#EEF2FF', color: '#4338CA',
                                        fontSize: 10, fontWeight: 600, borderRadius: 1 }}>
                                        {b.pm_name.split(' ')[0]}
                                      </Box>
                                    )}
                                    {b.report_to_location && b.report_to_location !== 'job_site' && (
                                      <Box sx={{ px: 0.75, py: 0.1, bgcolor: '#FEF3C7', color: '#92400E',
                                        fontSize: 10, fontWeight: 600, borderRadius: 1 }}>
                                        → {
                                          b.report_to_location === 'acs_westbury' ? 'Westbury' :
                                          b.report_to_location === 'acs_nyc'      ? 'NYC'      :
                                          (b.report_to_note || 'Other')
                                        }
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </Tooltip>
                            );
                          })
                        )}
                      </Box>
                    );
                  })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}

            {techs.length === 0 && !loading && (
              <Box sx={{ gridColumn: '1 / span 8', p: 6, textAlign: 'center' }}>
                <Typography sx={{ color: T.textMuted }}>No techs loaded.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Legend + footer */}
      <Stack direction="row" spacing={3} sx={{ mt: 2.5, px: 1, alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {['confirmed', 'suggested', 'flagged', 'needs_pm'].map((k) => {
          const s = statusStyle(k);
          return (
            <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <StatusDot status={k} />
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: T.textMuted }}>{s.label}</Typography>
            </Box>
          );
        })}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: T.textFaint, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon sx={{ fontSize: 14 }} />
          6 PM email cron is wired, dry-run only until approved
        </Typography>
      </Stack>

      {/* Close-Day dialog — pick a date, preview flagged CONFIRMED blocks, fire per-PM emails. */}
      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: `${T.radius}px`, fontFamily: T.fontStack } }}>
        <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${T.borderSoft}` }}>
          Close day
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <TextField
              type="date"
              label="Day to close"
              size="small"
              value={closeDate}
              onChange={(e) => { setCloseDate(e.target.value); loadClosePreview(e.target.value); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 200 }}
            />
            {closePreview && (
              <Typography sx={{ fontSize: 13, color: T.textMuted }}>
                {closePreview.last_closure
                  ? `Last closed ${new Date(closePreview.last_closure.closed_at).toLocaleString()} — ${closePreview.last_closure.pms_notified} PM(s) notified`
                  : 'Not yet closed'}
              </Typography>
            )}
          </Box>
          {closeError && <Alert severity="error" sx={{ mb: 2 }}>{closeError}</Alert>}
          {!closePreview && !closeError && <LinearProgress />}
          {closePreview && !closeResult && (
            <Box>
              <Typography sx={{ fontSize: 14, mb: 1.5 }}>
                <strong>{closePreview.flagged_total}</strong> confirmed assignment{closePreview.flagged_total === 1 ? '' : 's'} on this day with no task type and no notes.
                {closePreview.unassigned_count > 0 && ` (${closePreview.unassigned_count} have no PM assigned — those will NOT be emailed.)`}
              </Typography>
              {closePreview.pm_summaries.length === 0 ? (
                <Alert severity="success">All confirmed assignments on this day have a task or notes. Closing will just log the review; no emails will go out.</Alert>
              ) : (
                <Box sx={{ border: `1px solid ${T.borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.bg }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>PM</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>Flagged</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closePreview.pm_summaries.map((p) => (
                        <tr key={p.pm_id}>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>{p.pm_name}</td>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}`, textAlign: 'right', fontWeight: 600 }}>{p.block_count}</td>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}`, color: T.textMuted }}>{p.pm_email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
              <Typography sx={{ fontSize: 12, color: T.textMuted, mt: 2 }}>
                Each PM gets one email from <code>dispatch@audiocommand.com</code> listing their flagged blocks. <code>fieldops@audiocommand.com</code> is Cc'd on every send.
              </Typography>
            </Box>
          )}
          {closeResult && (
            <Box>
              <Alert severity={closeResult.send_results?.every((r) => r.ok) ? 'success' : 'warning'} sx={{ mb: 2 }}>
                Day closed. {closeResult.pms_notified} PM email{closeResult.pms_notified === 1 ? '' : 's'} sent.
              </Alert>
              {closeResult.send_results?.length > 0 && (
                <Box sx={{ border: `1px solid ${T.borderSoft}`, borderRadius: 1, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.bg }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>PM</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>Blocks</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closeResult.send_results.map((r) => (
                        <tr key={r.pm_id}>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}` }}>{r.pm_email}</td>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}`, textAlign: 'right' }}>{r.block_count}</td>
                          <td style={{ padding: '8px 12px', borderBottom: `1px solid ${T.borderSoft}`, color: r.ok ? '#15803D' : '#B91C1C' }}>
                            {r.ok ? 'Sent' : `Failed: ${r.error}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${T.borderSoft}` }}>
          <Button onClick={() => setCloseOpen(false)}>Close</Button>
          {closePreview && !closeResult && closePreview.pm_summaries.length > 0 && (
            <Button
              variant="contained"
              disabled={closeLoading}
              onClick={async () => {
                setCloseLoading(true); setCloseError(null);
                try {
                  const t = localStorage.getItem('token');
                  const r = await axios.post('/api/dispatch-preview/close-day',
                    { date: closePreview.date },
                    { headers: { Authorization: `Bearer ${t}` } });
                  setCloseResult(r.data);
                } catch (e) {
                  setCloseError(e.response?.data?.error || e.message);
                } finally {
                  setCloseLoading(false);
                }
              }}
              sx={{ bgcolor: '#1D3354', '&:hover': { bgcolor: '#15263F' } }}
            >
              {closeLoading ? 'Closing…' : `Close & notify ${closePreview.pm_summaries.length} PM${closePreview.pm_summaries.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConflictsDrawer
        open={conflictsOpen}
        onClose={() => setConflictsOpen(false)}
        conflicts={conflicts}
        onJump={jumpToConflict}
      />

      <BlockDetailDialog
        open={detailOpen}
        block={detailBlock}
        onClose={() => { setDetailOpen(false); setDetailBlock(null); }}
      />

      <BlockModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setModalFocusDate(null); }}
        techs={techs}
        pms={pms}
        library={library}
        authority={authority}
        me={me}
        existing={modalExisting}
        defaults={modalDefaults}
        focusDate={modalFocusDate}
        onSaved={() => { refreshBlocks(); refreshConflicts(); }}
      />

      <LaborRequestsDrawer
        open={requestsDrawerOpen}
        onClose={() => setRequestsDrawerOpen(false)}
        requests={laborRequests}
        onOpenRequest={(r) => {
          setRequestModalExisting(r);
          setRequestModalMode('fulfill');
          setRequestModalOpen(true);
          setRequestsDrawerOpen(false);
        }}
        onCreateNew={() => {
          setRequestModalExisting(null);
          setRequestModalMode('create');
          setRequestModalOpen(true);
          setRequestsDrawerOpen(false);
        }}
      />

      <LaborRequestModal
        open={requestModalOpen}
        mode={requestModalMode}
        existing={requestModalExisting}
        techs={techs}
        authority={authority}
        me={me}
        onClose={() => setRequestModalOpen(false)}
        onSaved={() => { refreshLaborRequests(); refreshBlocks(); }}
      />
    </Box>
  );
};

export default DispatchPreview;
